import { useEffect, useState } from 'react';
import { StockKind, StockLine, Warehouse } from '../models/StockLine';
import {
  ProductCategoryOption,
  productCategoryOptions,
} from '../models/ProductCategory';
import { useIntl } from 'react-intl';
import Select from 'react-select';
import {
  createStock,
  listProductVariants,
  listStock,
  removeStock,
  updateStock,
  downloadStockExcel,
} from '../services/StockLinesCRUD';
import { toast } from 'react-toastify';
import StockModal from './StockModal';
import getKindOptions from '../services/getKindOptions';
import getWarehouseOptions from '../services/getWarehouseOptions';
import getReasonOptions from '../services/getReasonOptions';
import { SaleableProductModel } from '../../b2b-orders';
import StockLogsModal from './StockLogsModal';
import { authUserSelector } from '../../auth';
import { useSelector } from 'react-redux';
import authorizedStockUsers from '../constants/authorizedStockUsers';

export default function StockList() {
  const intl = useIntl();
  const authUser = useSelector(authUserSelector);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] =
    useState<SaleableProductModel | null>(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | null>(
    null
  );
  const [selectedKind, setSelectedKind] = useState<StockKind | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<
    Array<ProductCategoryOption>
  >([]);
  const [stockLines, setStockLines] = useState<Array<StockLine>>([]);
  const [stockModalVisible, setStockModalVisible] = useState(false);
  const [stockLogsModalVisible, setStockLogsModalVisible] = useState(false);
  const [selectedStockLine, setSelectedStockLine] = useState<StockLine | null>(
    null
  );
  const warehouseOptions = getWarehouseOptions(intl);
  const kindOptions = getKindOptions(intl);
  const reasonOptions = getReasonOptions(intl);
  const productOptions = [
    {
      value: '',
      label: intl.formatMessage({ id: 'stock.all' }),
      disabled: false,
    },
    ...(products?.map((product) => ({
      value: product.id,
      label: `${product.name} - ${product.ean}`,
      disabled: false,
    })) || []),
  ];

  function getProductName(productId: number) {
    const productOption = productOptions.find(
      (option) => option.value === productId
    );
    return productOption?.label || '-';
  }

  async function fetchProducts() {
    try {
      setLoading(true);
      const { data } = await listProductVariants();
      setProducts(data);
    } catch (error) {
      console.warn(error);
    } finally {
      setLoading(false);
    }
  }

  const selectedCategoryValues = selectedCategories.map(
    (category) => category.value
  );
  const isFilteringByCategory = selectedCategoryValues.length > 0;

  async function fetchStockLines() {
    try {
      setLoading(true);
      const { data } = await listStock({
        kind: selectedKind,
        warehouse: selectedWarehouse,
        product: isFilteringByCategory ? undefined : selectedProduct?.id,
        categories: selectedCategoryValues,
      });
      setStockLines(data.sort((a, b) => a.id - b.id));
    } catch (error) {
      console.warn(error);
      toast.error(
        intl.formatMessage({
          id: 'stock.errorFetchingStockLines',
        })
      );
    } finally {
      setLoading(false);
    }
  }

  /*
  async function onRemove(stockLineId: number) {
    const hasConfirmed = window.confirm(
      intl.formatMessage({
        id: 'stock.confirmRemoveStockLine',
      })
    );
    if (!hasConfirmed) {
      return;
    }
    try {
      await removeStock(stockLineId);
      toast.success(
        intl.formatMessage({
          id: 'stock.stockLineRemoved',
        })
      );
      setStockLines((prev) =>
        prev.filter((stockLine) => stockLine.id !== stockLineId)
      );
    } catch (error) {
      console.warn(error);
      toast.error(
        intl.formatMessage({
          id: 'stock.errorRemovingStockLine',
        })
      );
    }
  }
*/

  async function onSaveStockLine(stockLine: StockLine) {
    setStockModalVisible(false);
    if (stockLine?.id) {
      updateStockLine(stockLine);
    } else {
      createStockLine(stockLine);
    }
  }

  async function createStockLine(stockLine: StockLine) {
    try {
      await createStock({
        product: stockLine.product,
        warehouse: stockLine.warehouse,
        kind: stockLine.kind,
        quantity: stockLine.quantity,
        reason: stockLine.reason,
      });
      toast.success(
        intl.formatMessage({
          id: 'stock.stockLineCreated',
        })
      );
      fetchStockLines();
    } catch (error) {
      console.warn(error);
      toast.error(
        intl.formatMessage({
          id: 'stock.errorCreatingStockLine',
        })
      );
    }
  }

  async function updateStockLine(stockLine: StockLine) {
    try {
      await updateStock({
        id: stockLine.id,
        product: stockLine.product,
        warehouse: stockLine.warehouse,
        kind: stockLine.kind,
        quantity: stockLine.quantity,
        reason: stockLine.reason,
      });
      toast.success(
        intl.formatMessage({
          id: 'stock.stockLineUpdated',
        })
      );
      fetchStockLines();
    } catch (error) {
      console.warn(error);
      toast.error(
        intl.formatMessage({
          id: 'stock.errorUpdatingStockLine',
        })
      );
    }
  }

  function onAddStockLine() {
    setSelectedStockLine(null);
    setStockModalVisible(true);
  }

  function viewLogs(stockLine) {
    setSelectedStockLine(stockLine);
    setStockLogsModalVisible(true);
  }

  function onEdit(stockLine: StockLine) {
    setSelectedStockLine(stockLine);
    setStockModalVisible(true);
  }

  async function onDownloadExcel() {
    try {
      setLoading(true);
      const blob = await downloadStockExcel({
        kind: selectedKind,
        warehouse: selectedWarehouse,
        product: isFilteringByCategory ? undefined : selectedProduct?.id,
        categories: selectedCategoryValues,
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `stock-${new Date().toISOString()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(
        intl.formatMessage({
          id: 'stock.excelDownloaded',
        })
      );
    } catch (error) {
      console.warn(error);
      toast.error(
        intl.formatMessage({
          id: 'stock.errorDownloadingExcel',
        })
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    const sortedProducts = products.sort((a, b) =>
      a.name.localeCompare(b.name)
    );
    if (sortedProducts?.length > 0) {
      setSelectedProduct(sortedProducts[0]);
    }
  }, [products?.length]);

  useEffect(() => {
    fetchStockLines();
  }, [selectedWarehouse, selectedKind, selectedProduct, selectedCategories]);

  return (
    <>
      <div className="card p-10">
        <div className="d-flex justify-content-between align-items-center mb-8">
          <h1>{intl.formatMessage({ id: 'stock.listTitle' })}</h1>
          <div className="d-flex gap-4">
            <button
              className="btn btn-success"
              onClick={onDownloadExcel}
              disabled={loading}
            >
              <span className="fas fa-file-excel me-2"></span>
              {intl.formatMessage({ id: 'stock.exportExcel' })}
            </button>
            {authorizedStockUsers.includes(authUser?.email) && (
              <button className="btn btn-primary" onClick={onAddStockLine}>
                <span className="fas fa-plus me-4"></span>
                {intl.formatMessage({ id: 'stock.addStock' })}
              </button>
            )}
          </div>
        </div>
        <div className="d-flex gap-4 align-items-center mb-8">
          <div>
            <label htmlFor="warehouse" className="form-label">
              {intl.formatMessage({ id: 'stock.warehouse' })}
            </label>
            <select
              id="warehouse"
              className="form-select"
              value={selectedWarehouse}
              onChange={(e) =>
                setSelectedWarehouse(
                  e.target.value === '-'
                    ? null
                    : (parseInt(e.target.value, 10) as unknown as Warehouse)
                )
              }
            >
              {warehouseOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="kind" className="form-label">
              {intl.formatMessage({ id: 'stock.kind' })}
            </label>
            <select
              id="kind"
              className="form-select"
              value={selectedKind}
              onChange={(e) =>
                setSelectedKind(
                  e.target.value === '-'
                    ? null
                    : (e.target.value as unknown as StockKind)
                )
              }
            >
              {kindOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="product" className="form-label">
              {intl.formatMessage({ id: 'stock.product' })}
            </label>
            <Select
              className="react-select-container"
              classNamePrefix="react-select"
              options={productOptions}
              value={
                productOptions.find(
                  (option) => option.value === selectedProduct?.id
                ) || null
              }
              isOptionDisabled={(option) => option.disabled}
              isDisabled={isFilteringByCategory}
              onChange={(option) => {
                const product = products.find(
                  (product) => product.id === option.value
                );
                setSelectedProduct(product || null);
                setSelectedCategories([]);
              }}
              placeholder={intl.formatMessage({
                id: 'orderDetail.searchProductPlaceholder',
              })}
            />
          </div>
          <div style={{ minWidth: 260 }}>
            <label htmlFor="category" className="form-label">
              {intl.formatMessage({ id: 'stock.category' })}
            </label>
            <Select
              inputId="category"
              className="react-select-container"
              classNamePrefix="react-select"
              isMulti
              isClearable
              options={productCategoryOptions}
              value={selectedCategories}
              onChange={(options) => {
                setSelectedCategories(
                  options as unknown as Array<ProductCategoryOption>
                );
                setSelectedProduct(null);
              }}
              placeholder={intl.formatMessage({ id: 'stock.all' })}
            />
          </div>
          {loading && (
            <div
              className="spinner-border spinner-border-sm text-primary mt-6 ms-4"
              role="status"
            />
          )}
        </div>
        <section>
          {!stockLines?.length && (
            <tr>
              <td colSpan={7} className="text-center">
                {intl.formatMessage({ id: 'stock.noStockLines' })}
              </td>
            </tr>
          )}
          {stockLines?.length > 0 && (
            <table className="table table-striped table-hover mt-4">
              <thead className="fw-bolder">
                <tr>
                  <th>{intl.formatMessage({ id: 'stock.product' })}</th>
                  <th>{intl.formatMessage({ id: 'stock.category' })}</th>
                  <th>{intl.formatMessage({ id: 'stock.warehouse' })}</th>
                  <th>{intl.formatMessage({ id: 'stock.kind' })}</th>
                  <th>{intl.formatMessage({ id: 'stock.reason' })}</th>
                  <th>{intl.formatMessage({ id: 'stock.quantity' })}</th>
                  <th>{intl.formatMessage({ id: 'stock.actions' })}</th>
                </tr>
              </thead>
              <tbody>
                {stockLines.map((stockLine, i) => (
                  <tr key={i} className="align-middle">
                    <td>{getProductName(stockLine.product)}</td>
                    <td>{stockLine.product_detail?.category || '-'}</td>
                    <td>
                      {warehouseOptions.find(
                        (option) => option.value === stockLine?.warehouse
                      )?.label || '-'}
                    </td>
                    <td>
                      {kindOptions.find(
                        (option) => option.value === stockLine?.kind
                      )?.label || '-'}
                    </td>
                    <td>
                      {reasonOptions.find(
                        (option) => option.value === stockLine?.reason
                      )?.label || '-'}
                    </td>
                    <td>{stockLine?.quantity}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-secondary me-4 px-4 py-2"
                        onClick={() => viewLogs(stockLine)}
                      >
                        <span className="fas fa-history me-2"></span>
                        {intl.formatMessage({ id: 'stock.viewLogs' })}
                      </button>
                      {authorizedStockUsers.includes(authUser?.email) && (
                        <button
                          className="btn btn-sm btn-primary px-4 py-2 me-4"
                          onClick={() => onEdit(stockLine)}
                        >
                          <span className="fas fa-pencil-alt me-2"></span>
                          {intl.formatMessage({ id: 'stock.edit' })}
                        </button>
                      )}
                      {/*
                        authorizedStockUsers.includes(authUser?.email) && <button 
                            className="btn btn-sm btn-danger px-4 py-2"
                            onClick={ () => onRemove(stockLine.id) }
                        >
                            <span className="fas fa-trash me-2"></span>
                            {intl.formatMessage({ id: 'stock.delete' })}
                        </button>
                        */}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
      <StockModal
        products={products}
        visible={stockModalVisible}
        onClose={() => setStockModalVisible(false)}
        onSave={onSaveStockLine}
        stockLine={selectedStockLine}
      />
      <StockLogsModal
        stockLine={selectedStockLine}
        products={products}
        onClose={() => setStockLogsModalVisible(false)}
        visible={stockLogsModalVisible}
      />
    </>
  );
}
